import { useParams, useNavigate } from "react-router-dom";
import products from "../../mocks/product.json";
import {
  Box,
  Button,
  Typography,
  Stack,
  Pagination,
  PaginationItem,
  IconButton,
  TextField,
  InputAdornment,
  Divider,
  Tooltip,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Nav from "../../components/Nav";
import Breadcrumb from "../../components/Breadcrumb";
import BannerImage from "../../assets/images/store-banner.png";
import { useState, useMemo } from "react";
import styles from "./index.module.css";

const pageSize = 5;

export default function SubcategoryList() {
  const { categoryId, productId } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  const category = products.find((c) => String(c.id) === categoryId);
  const product = category?.list.find((p) => String(p.id) === productId);
  const flattenSubcategories = product?.subcategories || [];

  const paginatedSubs = useMemo(() => {
    const offset = (page - 1) * pageSize;
    return flattenSubcategories.slice(offset, offset + pageSize);
  }, [page, flattenSubcategories]);

  if (!product) return <div>Product not found</div>;

  return (
    <Stack className={styles.landingPage} component="main" direction="column">
      <Stack className={styles.mainSection}>
        <Box className={styles.heroBanner}>
          <img src={BannerImage} alt="hero-banner" />
        </Box>
        <Box className={styles.content}>
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: "center", justifyContent: "space-between" }}
          >
            <TextField
              variant="outlined"
              size="small"
              placeholder="Search"
              sx={{
                height: "32px",
                "& .MuiOutlinedInput-root": {
                  height: "32px",
                  backgroundColor: "white",
                  borderRadius: 0,
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Stack color="primary">
                      <SearchIcon
                        sx={{
                          backgroundColor: "#0a9a73",
                          color: "white",
                          padding: "2px",
                        }}
                      />
                    </Stack>
                  </InputAdornment>
                ),
              }}
            />
            <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
              <Button
                variant="contained"
                size="small"
                sx={{ display: { xs: "none", sm: "block" } }}
              >
                Login | Pay Bill
              </Button>
              <Tooltip title="Login | Pay Bill">
                <IconButton
                  variant="contained"
                  size="large"
                  sx={{ color: "white", display: { xs: "flex", sm: "none" } }}
                >
                  <AccountCircleIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
          <Nav />
          <Breadcrumb
            links={[
              { title: "Home", url: "/" },
              { title: "Medical Supplies", url: "/medicalsupplies" },
              { title: category?.title || "Category", url: `/medicalsupplies/${categoryId}` },
              { title: product?.title || "Product" },
            ]}
          />
        </Box>
      </Stack>
      <Box sx={{ background: "white" }}>
        <Box className={styles.content}>
          <Stack
            direction="row"
            sx={{ alignItems: "center", justifyContent: "space-between", my: 2 }}
          >
            <Typography variant="h4">{product?.title}</Typography>
            <Button
              variant="outlined"
              onClick={() => navigate(`/medicalsupplies/${categoryId}`)}
              sx={{ mb: 3 }}
            >
              Back
            </Button>
          </Stack>

          <Stack spacing={2}>
            {paginatedSubs.map((sub) => (
              <Stack
                key={sub.id}
                onClick={() =>
                  navigate(`/medicalsupplies/${categoryId}/${product.id}/${sub.id}`)
                }
                direction={{ xs: "column", sm: "row" }}
                spacing={3}
                sx={{
                  padding: "22px",
                  boxShadow: "0px 3px 31px #4C31DC1A",
                  cursor: "pointer",
                  "&:hover": { boxShadow: "0px 0px 2px #20bfa9" },
                }}
              >
                <Box sx={{ minWidth: "230px", width: "230px", height: "230px" }}>
                  <img
                    src={product.img || "/assets/images/placeholder.png"}
                    alt={sub.title}
                    width="100%"
                    height="100%"
                    style={{ border: "1px solid lightgrey" }}
                  />
                </Box>
                <Stack>
                  <Typography variant="h5">{sub.title}</Typography>
                  {sub.description && (
                    <Typography variant="body1" sx={{ mt: 1 }}>
                      {sub.description}
                    </Typography>
                  )}
                </Stack>
              </Stack>
            ))}
          </Stack>
          <Stack sx={{ alignItems: "center", mt: 2, pb: 2 }}>
            <Pagination
              color="primary"
              count={Math.ceil(flattenSubcategories.length / pageSize)}
              page={page}
              onChange={(_, nextPage) => setPage(nextPage)}
              renderItem={(item) => (
                <PaginationItem
                  slots={{ previous: ArrowBackIcon, next: ArrowForwardIcon }}
                  {...item}
                />
              )}
            />
          </Stack>
        </Box>
      </Box>
    </Stack>
  );
}
